<script lang="ts" setup>
import type { AvatarColor, AvatarIllustration } from '~kui/types/kui';

interface Props {
  color?: AvatarColor | null;
  illustration: AvatarIllustration;
  hasBorder?: boolean;
  hasPadding?: boolean;
  overflowHidden?: boolean;
  as?: 'div' | 'span';
}

const props = withDefaults(defineProps<Props>(), {
  color: null,
  hasBorder: false,
  hasPadding: false,
  overflowHidden: true,
  as: 'div',
});

const hasNoColor = computed(() => props.color === null);

const variantClasses = computed(() =>
  // min-h-0 disables the aspect-ratio content-based minimum, which would otherwise
  // stretch the circle into an oval when overflow is visible and the illustration is portrait.
  twMerge(cva('rounded-full aspect-square min-h-0 flex justify-center items-center', {
    variants: {
      overflowHidden: {
        true: 'overflow-hidden',
        false: '',
      },
      hasNoColor: {
        true: 'bg-background',
        false: '',
      },
      hasPadding: {
        true: 'p-5',
        false: '',
      },
      color: {
        lime: 'bg-secondary',
        coral: 'bg-brand-coral',
        pink: 'bg-brand-pink',
        purple: 'bg-brand-purple',
        orange: 'bg-brand-orange',
        blue: 'bg-brand-blue',
        yellow: 'bg-brand-yellow',
        white: 'bg-white',
      },
      hasBorder: {
        true: 'border-5 border-white',
        false: '',
      },
    },
  })({
    overflowHidden: props.overflowHidden,
    hasNoColor: hasNoColor.value,
    hasPadding: props.hasPadding,
    color: props.color,
    hasBorder: props.hasBorder,
  })),
);
</script>

<template>
  <component :is="as" :class="variantClasses">
    <NuxtImg
      :src="`/images/illustrations/${illustration}.webp`"
      role="presentation"
      alt=""
      class="h-full w-full object-contain"
    />
  </component>
</template>
